/**
 * @fileoverview A faixa do atraso no pagamento (31/10/2026, etapa 10 dos
 * planos), no topo de todas as telas:
 * - a fatura vencida, antes da suspensão: só quem cuida da cobrança (o servidor
 *   só manda o atraso para ele), com o prazo e o caminho até Faturamento.
 *   Fechada, volta no dia seguinte;
 * - a suspensão: todos, sem fechar. Quem cuida da cobrança vê o caminho para
 *   pagar; os demais, para falar com o administrador;
 * - o prazo da escolha do que fica depois do fim de uma exceção de limite
 *   (etapa 11): só quem cuida da cobrança. Fechada, volta no dia seguinte.
 * Lida do contexto da cobrança (GET /account/status), que relê quando o
 * servidor avisa pelo socket. O super não vê.
 */

import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { AlertTriangle, X } from "lucide-react";
import { useHistory } from "react-router-dom";

import { useBilling } from "../../../context/Billing/BillingContext";
import { i18n } from "../../../translate/i18n";
import { formatSaoPauloDate, formatUtcDate } from "../../../utils/formatLocaleDate";

const t = (key, options) => i18n.t(`billingStatusBanner.${key}`, options);

const BILLING_PATH = "/settings/billing";

// Fechada, volta no dia seguinte (o dia de quem está vendo, não o de UTC).
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};
const dismissKey = (since) => `billingStatusBanner:${since}:${today()}`;

const isDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch (err) {
    return false;
  }
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    bar: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px 8px 20px",
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.45,
      borderBottom: "1px solid transparent",
      "& > *": { animation: "$in 180ms ease-out" },
      "@media (prefers-reduced-motion: reduce)": { "& > *": { animation: "none" } },
      [theme.breakpoints.down("sm")]: { padding: "8px 6px 8px 16px", fontSize: 12.5, flexWrap: "wrap" },
    },
    overdue: dark
      ? { background: "#221a0a", color: "#FDE68A", borderBottomColor: "rgba(251,191,36,0.16)" }
      : { background: "#FFFBEB", color: "#78350F", borderBottomColor: "#FDE68A" },
    suspended: dark
      ? { background: "#241111", color: "#FECACA", borderBottomColor: "rgba(248,113,113,0.18)" }
      : { background: "#FEF2F2", color: "#7F1D1D", borderBottomColor: "#FECACA" },
    icon: { flexShrink: 0 },
    text: { flex: 1, minWidth: 0 },
    action: {
      flexShrink: 0,
      border: "none",
      background: "transparent",
      padding: "4px 6px",
      fontFamily: "inherit",
      fontSize: "inherit",
      fontWeight: 650,
      color: "inherit",
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      borderRadius: 6,
      "&:focus-visible": { outline: "1px solid currentColor" },
    },
    close: {
      width: 30,
      height: 30,
      flexShrink: 0,
      border: "none",
      borderRadius: 8,
      background: "transparent",
      color: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      opacity: 0.7,
      transition: "opacity 150ms, background 150ms",
      "&:hover": { opacity: 1, background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
      "&:focus-visible": { outline: "1px solid currentColor", opacity: 1 },
    },
    "@keyframes in": {
      from: { opacity: 0, transform: "translateY(-4px)" },
      to: { opacity: 1, transform: "none" },
    },
  };
});

const BillingStatusBanner = ({ managesBilling, isSuper }) => {
  const classes = useStyles();
  const history = useHistory();
  const { billing } = useBilling();
  const [closed, setClosed] = useState([]);

  if (isSuper || !billing) return null;
  const { suspension, overdue, adequacyDue } = billing;
  const goToBilling = () => history.push(BILLING_PATH);

  if (suspension) {
    const internal = suspension.scope === "internal_chat";
    const text = managesBilling ? t(internal ? "suspendedInternalAdmin" : "suspendedAdmin") : t(internal ? "suspendedInternalMember" : "suspendedMember");
    return (
      <div className={`${classes.bar} ${classes.suspended}`} role="alert">
        <AlertTriangle size={16} className={classes.icon} aria-hidden="true" />
        <span className={classes.text}>{text}</span>
        {managesBilling && (
          <button type="button" className={classes.action} onClick={goToBilling}>
            {t("pay")}
          </button>
        )}
      </div>
    );
  }

  if (!managesBilling) return null;
  // O atraso vem antes; fechado (até amanhã), aparece o prazo da escolha, se houver. Cada um fecha por si.
  const bar = [overdue && { kind: "overdue", key: dismissKey(overdue.since) }, adequacyDue && { kind: "due", key: dismissKey(`due-${adequacyDue}`) }].find(
    (candidate) => candidate && !closed.includes(candidate.key) && !isDismissed(candidate.key)
  );
  if (!bar) return null;
  const close = () => {
    try {
      localStorage.setItem(bar.key, "1");
    } catch (err) {
      // Sem o armazenamento do navegador, a faixa fica fechada só até recarregar.
    }
    setClosed((current) => [...current, bar.key]);
  };

  return (
    <div className={`${classes.bar} ${classes.overdue}`} role="status">
      <AlertTriangle size={16} className={classes.icon} aria-hidden="true" />
      <span className={classes.text}>
        {bar.kind === "due"
          ? t("adequacyDue", { date: formatSaoPauloDate(adequacyDue) })
          : overdue.payBy
            ? t(overdue.scope === "internal_chat" ? "overdueInternal" : "overdue", { date: formatUtcDate(overdue.payBy) })
            : t("overdueNoDeadline")}
      </span>
      <button type="button" className={classes.action} onClick={goToBilling}>
        {bar.kind === "overdue" ? t("pay") : t("choose")}
      </button>
      <button type="button" className={classes.close} onClick={close} aria-label={t("close")} title={t("close")}>
        <X size={15} />
      </button>
    </div>
  );
};

export default BillingStatusBanner;
